// Appearance: light/dark, and the deployment's accent colour.
//
// Both are DOM writes, which is why they live here rather than in brand.ts — the
// Worker imports that module to validate a saved brand, so it has to stay free of
// anything browser-shaped.
//
// Light/dark by toggling a `dark` class on <html>, which the CSS keys off.
//
// Inside General Text this deferred to the shell, because the app was a frame in
// someone else's chrome and disagreeing with it looked broken. Standalone there is
// no shell to defer to.
//
// **Two modes, and light is the default.** Following the OS was the old behaviour
// and it was worse than it sounds: a team on a dark-by-default machine got a
// theme nobody chose, and — because there was no control anywhere — no way to say
// otherwise. A named default plus a switch beats an inference with no override.
// The client's seat is always light regardless; see initTheme.

import { displayName, type Brand } from './brand'

const KEY = 'crum.theme'
export type Mode = 'light' | 'dark'

/** The default. Stated once here rather than implied by a fallback. */
const DEFAULT_MODE: Mode = 'light'

/** The RAIL colour in each mode, mirrored in the pre-paint script in index.html.
 *  Both places have to agree, and it is the rail rather than the page ground because
 *  this is what paints the strip behind the iOS status bar, directly above the
 *  header — see the `display-mode: standalone` block in global.css. */
// design-system-exempt: mirrored in index.html's pre-paint script, which runs before
// any stylesheet exists. A token here would be a token nothing can resolve yet.
const GROUND: Record<Mode, string> = { light: '#e8e0d0', dark: '#16120e' }

function setDark(on: boolean) {
  const root = document.documentElement
  root.classList.toggle('dark', on)
  // Keep the status bar and the browser chrome on the app's actual background. The
  // inline script in index.html does this before first paint; this is the same write
  // for every change afterwards, so toggling the theme moves the bar with it.
  const bg = GROUND[on ? 'dark' : 'light']
  root.style.backgroundColor = bg
  root.style.colorScheme = on ? 'dark' : 'light'
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bg)
}

export function themeMode(): Mode {
  try {
    return localStorage.getItem(KEY) === 'dark' ? 'dark' : DEFAULT_MODE
  } catch {
    // A blocked or full localStorage must not stop the app painting.
    return DEFAULT_MODE
  }
}

export function setThemeMode(mode: Mode): void {
  try {
    localStorage.setItem(KEY, mode)
  } catch {
    // Ignore: the class below still applies for this session.
  }
  setDark(mode === 'dark')
}

export function toggleTheme(): Mode {
  const next: Mode = themeMode() === 'dark' ? 'light' : 'dark'
  setThemeMode(next)
  return next
}

/**
 * Resolve the theme before first paint.
 *
 * The client's seat is forced light: it is a document someone was sent, and it
 * should look the same to everyone who opens the link rather than inheriting a
 * preference that belongs to a different piece of software.
 */
export function initTheme(): void {
  const isClientSeat = typeof location !== 'undefined' && location.pathname.startsWith('/r/')
  setDark(!isClientSeat && themeMode() === 'dark')
}

/**
 * Push the accent into CSS custom properties. The palette is derived rather than
 * configured: a team picks one colour, not eleven, and a set of ramps computed
 * from it stays internally consistent in a way eleven hand-picked values do not.
 *
 * **Two accents, not one, because a colour picked on paper is not a colour that
 * works on charcoal.** The swatches in Settings are chosen against the light
 * ground; dropped unchanged onto the dark one, a rust or a navy lands around 3:1
 * and the app's own links become the least legible thing on the screen. So the
 * brand hex is written as `--accent-l` and a lifted twin as `--accent-d`, and
 * global.css picks between them per theme — which also means toggling the theme
 * needs no second call here.
 *
 * The lift is the same move the built-in pair already makes by hand: ember goes
 * #a8501f → #e08a4c and marine #1d5f6a → #6fb4bd, and in both cases the hue is
 * unchanged and the lightness lands at 59%.
 */
export function applyBrand(brand: Brand): void {
  const root = document.documentElement
  const light = hexToRgb(brand.accent)
  const darkHex = liftForDark(light)
  const dark = hexToRgb(darkHex)
  root.style.setProperty('--accent-l', brand.accent)
  root.style.setProperty('--accent-d', darkHex)
  root.style.setProperty('--accent-contrast-l', contrastOn(light))
  root.style.setProperty('--accent-contrast-d', contrastOn(dark))
  document.title = displayName(brand)
}

interface Rgb {
  r: number
  g: number
  b: number
}

function hexToRgb(hex: string): Rgb {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  }
}

/** Ink that reads on the accent: chosen from its luminance rather than guessed.
 *  Warm ink, not the cool slate this used to return — every other neutral in the
 *  app is biased toward ember and one cool one in the set is visible. */
function contrastOn({ r, g, b }: Rgb): string {
  // design-system-exempt: this is the value WRITTEN INTO --accent-contrast, so it
  // cannot itself be a token.
  return luminance(r, g, b) > 0.55 ? '#241f19' : '#ffffff'
}

/** Relative luminance, so text on the accent is chosen rather than guessed. */
function luminance(r: number, g: number, b: number): number {
  const channel = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** The dark-ground twin of an accent: same hue, lightness at 59%, saturation
 *  capped so a vivid pick does not glow. A colour already that light is left
 *  alone — it was chosen for a dark ground to begin with. */
function liftForDark(rgb: Rgb): string {
  const { h, s, l } = rgbToHsl(rgb)
  if (l >= 0.55) return hslToHex(h, s, l)
  return hslToHex(h, Math.min(s, 0.7), 0.59)
}

function rgbToHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const [rd, gd, bd] = [r / 255, g / 255, b / 255] as [number, number, number]
  const max = Math.max(rd, gd, bd)
  const min = Math.min(rd, gd, bd)
  const l = (max + min) / 2
  const d = max - min
  if (d === 0) return { h: 0, s: 0, l }
  const s = d / (1 - Math.abs(2 * l - 1))
  const h =
    max === rd
      ? 60 * (((gd - bd) / d + 6) % 6)
      : max === gd
        ? 60 * ((bd - rd) / d + 2)
        : 60 * ((rd - gd) / d + 4)
  return { h, s, l }
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  const seg = Math.floor(h / 60) % 6
  const rgb: [number, number, number] =
    seg === 0
      ? [c, x, 0]
      : seg === 1
        ? [x, c, 0]
        : seg === 2
          ? [0, c, x]
          : seg === 3
            ? [0, x, c]
            : seg === 4
              ? [x, 0, c]
              : [c, 0, x]
  const hex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${hex(rgb[0])}${hex(rgb[1])}${hex(rgb[2])}`
}
